import {vec3} from "_src/libs/gl-matrix";

const vec3Tmp = vec3.create();

export function buildNormals(indices, positions, normals, normalize) {
  for (let i = 0; i < indices.length; i += 3) {
    const ind0 = indices[i];
    const ind1 = indices[i + 1];
    const ind2 = indices[i + 2];

    const triangleNormal = calcTriangleNormal(vec3Tmp, i, indices, positions);

    const offset0 = 3 * ind0;
    const offset1 = 3 * ind1;
    const offset2 = 3 * ind2;

    normals[offset0] += triangleNormal[0];
    normals[offset0 + 1] += triangleNormal[1];
    normals[offset0 + 2] += triangleNormal[2];

    normals[offset1] += triangleNormal[0];
    normals[offset1 + 1] += triangleNormal[1];
    normals[offset1 + 2] += triangleNormal[2];

    normals[offset2] += triangleNormal[0];
    normals[offset2 + 1] += triangleNormal[1];
    normals[offset2 + 2] += triangleNormal[2];
  }

  if (normalize) {
    normalizeNormals(normals);
  }
}

function normalizeNormals(normals) {
  for (let i = 0; i < normals.length; i += 3) {
    const sqrtLen = Math.sqrt(normals[i] * normals[i] + normals[i+1] * normals[i+1] + normals[i+2] * normals[i+2]);
    if (sqrtLen != 0.0) {
      normals[i] /= sqrtLen;
      normals[i + 1] /= sqrtLen;
      normals[i + 2] /= sqrtLen;
    }
  }
}

export const rebuildVerticesNormalsMask = (function() {
  const vec3Tmp = vec3.create();

  function markVerticesToRecalculate(indices, normals, mask) {

    for (let i = 0; i < indices.length; i += 3) {

      const ind0 = indices[i];
      const ind1 = indices[i + 1];
      const ind2 = indices[i + 2];

      const m0 = mask[ind0];
      const m1 = mask[ind1];
      const m2 = mask[ind2];

      if (m0 == 1 || m1 == 1 || m2 == 1) {

        const offset0 = ind0 * 3;
        const offset1 = ind1 * 3;
        const offset2 = ind2 * 3;

        normals[offset0] = 0.0;
        normals[offset0 + 1] = 0.0;
        normals[offset0 + 2] = 0.0;

        normals[offset1] = 0.0;
        normals[offset1 + 1] = 0.0;
        normals[offset1 + 2] = 0.0;

        normals[offset2] = 0.0;
        normals[offset2 + 1] = 0.0;
        normals[offset2 + 2] = 0.0;

        if (!m0) mask[ind0] = 2;

        if (!m1) mask[ind1] = 2;

        if (!m2) mask[ind2] = 2;

        //Note: mask[i]=2 will be unset in chunk.averageNormals

      }

    }

  }

  function normalizeNormalsByMask(normals, mask) {

    for (let i = 0; i < mask.length; ++i) {

      if (mask[i]) {

        const offset = i * 3;
        const normal = vec3.set(vec3Tmp, normals[offset], normals[offset + 1], normals[offset +2]);
        vec3.normalize(normal, normal);

        normals[offset] = normal[0];
        normals[offset + 1] = normal[1];
        normals[offset + 2] = normal[2];

      }

    }

  }

  return function(indices, positions, normals, mask, normalize) {

    markVerticesToRecalculate(indices, normals, mask);

    for (let i = 0; i < indices.length; i += 3) {

      const ind0 = indices[i];
      const ind1 = indices[i + 1];
      const ind2 = indices[i + 2];

      const m0 = mask[ind0];
      const m1 = mask[ind1];
      const m2 = mask[ind2];

      if (!m0 && !m1 && !m2) {
        continue;
      }

      const triangleNormal = calcTriangleNormal(vec3Tmp, i, indices, positions);

      if (m0) {
        const offset0 = ind0 * 3;

        normals[offset0] += triangleNormal[0];
        normals[offset0 + 1] += triangleNormal[1];
        normals[offset0 + 2] += triangleNormal[2];
      }

      if (m1) {
        const offset1 = ind1 * 3;

        normals[offset1] += triangleNormal[0];
        normals[offset1 + 1] += triangleNormal[1];
        normals[offset1 + 2] += triangleNormal[2];
      }


      if (m2) {
        const offset2 = ind2 * 3;

        normals[offset2] += triangleNormal[0];
        normals[offset2 + 1] += triangleNormal[1];
        normals[offset2 + 2] += triangleNormal[2];
      }

    }

    if (normalize) {
      normalizeNormalsByMask(normals, mask);
    }

  }
})();

export function invertVerticesNormals(verticesNormals, indices) {
  if (indices) {
    for (let i = 0; i+2 < indices.length; i += 3) {
      //swap(indices[i+1], indices[i+2]);
      indices[i + 1] = indices[i + 1] ^ indices[i + 2];
      indices[i + 2] = indices[i + 1] ^ indices[i + 2];
      indices[i + 1] = indices[i + 1] ^ indices[i + 2];
    }
  }

  if (verticesNormals) {
    for (let i = 0; i < verticesNormals.length; i++) {
      verticesNormals[i] *= -1;
    }
  }
}

const calcTriangleNormal = (function() {

  const edge1 = vec3.create();
  const edge2 = vec3.create();

  return function (dest, indOffset, indices, positions) {

    // Calculates a non-normalized normal

    const ind0 = indices[indOffset];
    const ind1 = indices[indOffset + 1];
    const ind2 = indices[indOffset + 2];

    const offset0 = 3 * ind0;
    const offset1 = 3 * ind1;
    const offset2 = 3 * ind2;

    const x0 = positions[offset0];
    const y0 = positions[offset0 + 1];
    const z0 = positions[offset0 + 2];

    vec3.set(edge1, positions[offset1] - x0,
      positions[offset1 + 1] - y0,
      positions[offset1 + 2] - z0);

    vec3.set(edge2, positions[offset2] - x0,
      positions[offset2 + 1] - y0,
      positions[offset2 + 2] - z0);

    vec3.cross(dest, edge1, edge2);

    return dest;
  }
})();
